import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * NEW BLOCK TYPE — product_grid (storefront listings)
 *
 * Iter 175c — Section Audit. title · tagline · per-product name/price/
 * category, filter sidebar titles, gradient-promo titles/taglines/CTA
 * labels are all inline-editable.
 * ════════════════════════════════════════════════════════════════════════ */
export function ProductGridBlock({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'three-col-cards';
  const products = c.products || Array.from({length:6}).map((_,i)=>({ name:`Product ${i+1}`, price:`$${(i+1)*15}`, category:'Category' }));

  const cols = layout === 'four-col-clean' ? 4 : 3;

  if (layout === 'category-with-filter-sidebar') {
    const filters = c.filters || [
      { title: 'Category', options: ['All', 'Featured', 'New', 'Sale'] },
      { title: 'Price', options: ['Under $50', '$50–$100', 'Over $100'] },
      { title: 'Color', options: ['Red', 'Blue', 'Black'] },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <aside className="md:col-span-1 space-y-6">
            <p className="text-xs uppercase tracking-[0.18em] opacity-60 font-bold">Filters</p>
            {filters.map((f,i) => (
              <div key={i}>
                <E block={block} onEdit={onEdit} field={`filters.${i}.title`} value={f.title} tag="p" className="text-sm font-bold mb-2" placeholder="Filter" />
                <ul className="space-y-1.5">
                  {f.options.map((o,oi) => (
                    <li key={oi} className="text-sm opacity-75">
                      <label className="cursor-pointer hover:opacity-100"><input type="checkbox" className="mr-2" />
                        <E block={block} onEdit={onEdit} field={`filters.${i}.options.${oi}`} value={o} tag="span" placeholder="Option" />
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </aside>
          <div className="md:col-span-3">
            <E block={block} onEdit={onEdit} field="title" value={c.title || 'Browse Collection'} tag="h2" className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {products.slice(0,9).map((p,i)=>(
                <div key={i} className="group cursor-pointer">
                  <div className="aspect-square overflow-hidden">
                    {p.image ? <SafeImg src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400" />}
                  </div>
                  <E block={block} onEdit={onEdit} field={`products.${i}.name`} value={p.name} tag="p" className="mt-3 text-sm font-bold" placeholder="Product name" />
                  <E block={block} onEdit={onEdit} field={`products.${i}.price`} value={p.price} tag="p" className="text-sm" style={{ color: theme.accent }} placeholder="$0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Products — NEW `dual-collection-split` ──
  // Two side-by-side "collection" cards. Each card = big lifestyle image
  // + collection name + short line + shop CTA. Ideal for shops with 2
  // clear collections (e.g. Men/Women, Winter/Summer, Fine/Everyday).
  if (layout === 'dual-collection-split') {
    const collections = c.collections || [
      { name: 'Fine Jewelry', tagline: 'Heirloom pieces, hand-set.', cta: 'Shop the Fine', image: '' },
      { name: 'Everyday',     tagline: 'Made for wearing all week long.', cta: 'Shop Everyday',   image: '' },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-4 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-3">
          {collections.slice(0, 2).map((col, i) => (
            <div key={i} className="group relative overflow-hidden min-h-[480px]" style={{ borderRadius: theme.radius || 16 }}>
              {col.image
                ? <SafeImg src={col.image} alt={col.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                : <div className="absolute inset-0" style={{ background: i === 0 ? 'linear-gradient(135deg,#7C2D12,#0E0E23)' : 'linear-gradient(135deg,#312E81,#A78BFA)' }} />}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.65) 100%)' }} />
              <div className="relative h-full flex flex-col justify-end p-10 text-white">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70">Collection {String(i + 1).padStart(2, '0')}</p>
                <E block={block} onEdit={onEdit} field={`collections.${i}.name`} value={col.name} tag="h3" className="mt-3 text-4xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Collection name" />
                <E block={block} onEdit={onEdit} field={`collections.${i}.tagline`} value={col.tagline} tag="p" className="mt-3 text-white/85 max-w-sm" placeholder="Short line" />
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
                  <E block={block} onEdit={onEdit} field={`collections.${i}.cta`} value={col.cta} tag="span" placeholder="Shop the drop" />
                  <span aria-hidden>→</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Products — NEW `editorial-lookbook` ──
  // Magazine-style spread: big hero product image LEFT + editorial title/
  // description RIGHT with 3 numbered thumbnails below. Perfect for
  // brands wanting a fashion-lookbook feel.
  if (layout === 'editorial-lookbook') {
    const hero = products[0] || { name: 'The Editorial', price: '$220', image: '' };
    const thumbs = products.slice(1, 4);
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 items-start">
          <div className="relative">
            <p className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.3em] font-bold text-white/85 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">Issue 04 · Spring &rsquo;26</p>
            <div className="relative aspect-[4/5] overflow-hidden" style={{ borderRadius: theme.radius || 12 }}>
              {hero.image
                ? <SafeImg src={hero.image} alt={hero.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full" style={{ background: 'linear-gradient(135deg,#292524,#F5F5F4)' }} />}
            </div>
          </div>
          <div className="md:pt-10">
            <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'THE LOOKBOOK'} tag="p" className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }} placeholder="THE LOOKBOOK" />
            <E block={block} onEdit={onEdit} field="title" value={c.title || 'Quiet objects, loud stories.'} tag="h2" className='mt-3 text-5xl md:text-6xl font-bold leading-[1.02] font-["Playfair_Display","Georgia",serif]' placeholder="Title" />
            <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || 'Six new pieces. One long weekend of storytelling. Every purchase supports a Native maker.'} tag="p" className="mt-5 text-lg opacity-80 leading-relaxed max-w-md" placeholder="Editorial description" />
            <div className="mt-10 grid grid-cols-3 gap-4">
              {(thumbs.length ? thumbs : [{name:'Cuff'},{name:'Ring'},{name:'Charm'}]).map((p, i) => (
                <div key={i} className="group">
                  <div className="relative aspect-square overflow-hidden" style={{ borderRadius: 6 }}>
                    {p.image
                      ? <SafeImg src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500" />
                      : <div className="w-full h-full" style={{ background: `hsl(${(i + 1) * 60}, 20%, 78%)` }} />}
                    <p className="absolute top-2 left-2 text-[9px] font-mono font-bold text-white bg-black/60 px-1.5 py-0.5 rounded">0{i + 1}</p>
                  </div>
                  <E block={block} onEdit={onEdit} field={`products.${i + 1}.name`} value={p.name} tag="p" className="mt-2 text-xs font-bold" placeholder="Piece" />
                  {p.price && <E block={block} onEdit={onEdit} field={`products.${i + 1}.price`} value={p.price} tag="p" className="text-xs opacity-70" placeholder="$0" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'gradient-promo-twin') {
    const promos = c.promos || [
      { title: 'Collect your unique feature', tagline: 'Limited edition products.', button: 'Shop Now' },
      { title: 'Take 75% Discount', tagline: 'Storewide sale ends Sunday.', button: 'Browse Sale' },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {promos.slice(0,2).map((p,i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden p-10 text-white min-h-[260px] flex flex-col justify-end" style={{
              background: i === 0
                ? 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)'
                : 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
              borderRadius: theme.radius || 16,
            }}>
              {p.image && <SafeImg src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
              <div className="relative">
                <E block={block} onEdit={onEdit} field={`promos.${i}.title`} value={p.title} tag="h3" className="text-2xl md:text-3xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Promo headline" />
                <E block={block} onEdit={onEdit} field={`promos.${i}.tagline`} value={p.tagline || ''} tag="p" className="mt-2 opacity-90 text-sm" placeholder="Promo tagline" />
                <span className="mt-5 inline-block px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white text-gray-900 cursor-text">
                  <E block={block} onEdit={onEdit} field={`promos.${i}.button`} value={p.button || 'Shop'} tag="span" placeholder="Button" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto">
        <E block={block} onEdit={onEdit} field="title" value={c.title || 'Best-Selling Products'} tag="h2" className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
        <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="text-center mt-3 opacity-70 max-w-xl mx-auto" placeholder="Tagline" />
        <div className={`mt-10 grid grid-cols-2 md:grid-cols-${cols} gap-6`}>
          {products.slice(0, cols*2).map((p,i)=>(
            <div key={i} className="group cursor-pointer">
              <div className="aspect-square overflow-hidden">
                {p.image ? <SafeImg src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400" />}
              </div>
              <E block={block} onEdit={onEdit} field={`products.${i}.category`} value={p.category || ''} tag="p" className="mt-3 text-[10px] uppercase tracking-wider opacity-60 font-bold" placeholder="Category" />
              <E block={block} onEdit={onEdit} field={`products.${i}.name`} value={p.name} tag="p" className="mt-1 font-bold text-sm" placeholder="Product name" />
              <E block={block} onEdit={onEdit} field={`products.${i}.price`} value={p.price} tag="p" className="mt-1 text-sm" style={{ color: theme.accent }} placeholder="$0" />
              {layout === 'with-color-swatches' && (
                <div className="mt-2 flex gap-1.5">{(p.swatches||['#EF4444','#3B82F6','#10B981']).map((s,si)=><span key={si} className="w-3 h-3 rounded-full border border-gray-300" style={{ background:s }} />)}</div>
              )}
              {layout === 'three-col-cards' && <button className="mt-2 text-xs font-bold uppercase tracking-wider" style={{ color: theme.accent }}>Buy Now →</button>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
